import { useQuery } from '@tanstack/react-query'
import { useEffect, useState, type ReactNode } from 'react'
import {
  cqSpeedQuery,
  type CqBaseline,
  type CqPage,
  type CqSpeedPage,
  type CqSpeedTest,
  type CqVerdictLine,
} from '../api/connectionQuality'
import { DeviceFloorPosition, useHasFloorPosition } from '../floorplan/DeviceFloorPosition'
import { CqAccessPoints } from './CqAccessPoints'
import { CQ_COLORS, CqChart, CqChartSync, type CqChartSeries } from './CqChart'
import { CqChecksCategory } from './CqChecks'
import { CqPath } from './CqPath'
import { CqSpeed } from './CqSpeed'
import { CqWifi, signalGuides } from './CqWifi'
import { cqSeries, CqVerdictBox, CqWifiNone, LINES, tcpFormat } from './DeviceConnectionQuality'
import './CqGrid.css'

/**
 * Vederea Grid (compacta, implicita) a paginii mari Connection Quality.
 *
 * Sub concluzie, pe categorii, fiecare cu titlul ei (Connection, Wi-Fi,
 * Web & TCP, Speed, Path, apoi Where is this device, daca aparatul e pe un
 * plan). O categorie incepe cu cardurile ei de rezumat, cu starea colorata
 * ok / atentie / problema; fara date - gri, "no data". Un clic pe card
 * derulaza la categoria lui. Speed are cate un card pe cifra (download,
 * upload, latenta, RPM, bufferbloat); Path n-are card - traseul e chiar sub
 * titlu. Verificarile (MTU, DNS...) sunt prima categorie, imediat sub
 * concluzie, cate un card (CqChecksCategory, 30 septembrie 2026).
 *
 * Dupa carduri, graficele categoriei unul langa altul (2-3 pe rand pe ecran
 * lat, unul sub ~900 px), fiecare cu Expand: graficul trece pe toata latimea
 * categoriei, mai inalt; Close ori Escape il strange la loc. Tabelele si
 * sectiunile (Speed, Path, planul) stau intregi, pe toata latimea, fara
 * derulare inauntru. Access points sta in dreapta graficului de semnal Wi-Fi,
 * pe acelasi rand (sub ~1100 px trece dedesubt). Graficele de timp
 * stau toate sub acelasi `CqChartSync`, deci zoom-ul tras cu mouse-ul si linia
 * de hover merg si pe cele mici, si pe cel deschis, pe aceeasi axa.
 *
 * Aceleasi componente ca vederea Full si fila fisei - nimic nu se calculeaza
 * de doua ori; aici doar se aseaza altfel.
 */

/** `plain` = o cifra fara prag (download, upload...): fara culoare, fara eticheta. */
export type CqCardState = 'ok' | 'warn' | 'bad' | 'none' | 'plain'

export type CqPanelKey = 'latency' | 'loss' | 'mos' | 'tcp' | 'https' | 'wifi' | 'aps' | 'speed' | 'path' | 'where'

/** Categoriile Grid-ului, in ordinea de pe ecran; fiecare isi poarta cardurile. */
export type CqCategoryKey = 'connection' | 'wifi' | 'web' | 'speed' | 'path' | 'where'

export const CQ_CATEGORIES: ReadonlyArray<{ key: CqCategoryKey; title: string; icon: string }> = [
  { key: 'connection', title: 'Connection', icon: 'fas fa-network-wired' },
  { key: 'wifi', title: 'Wi-Fi', icon: 'fas fa-wifi' },
  { key: 'web', title: 'Web & TCP', icon: 'fas fa-globe' },
  { key: 'speed', title: 'Speed', icon: 'fas fa-tachometer-alt' },
  { key: 'path', title: 'Path', icon: 'fas fa-route' },
  { key: 'where', title: 'Where is this device', icon: 'fas fa-map-marker-alt' },
]

export type CqCard = {
  key: string
  title: string
  state: CqCardState
  value: string
  detail: string
  category: CqCategoryKey
}

const STATE_TEXT: Record<CqCardState, string> = { ok: 'ok', warn: 'attention', bad: 'problem', none: 'no data', plain: '' }

/**
 * Pragurile de "atentie" de sub cele de problema ale concluziei (Verdict.php):
 * o linie e in atentie cand pierde ceva, dar sub pragul de problema, ori cand
 * latenta trece de 70% din pragul ei, ori cand e peste normalul aparatului.
 */
const WARN_SHARE = 0.7

function round(n: number, digits = 0): string {
  const f = Math.pow(10, digits)
  return String(Math.round(n * f) / f)
}

function average(values: Array<number | null | undefined>): number | null {
  const ok = values.filter((v): v is number => typeof v === 'number')
  return ok.length ? ok.reduce((a, b) => a + b, 0) / ok.length : null
}

export function lineCard(
  key: 'gateway' | 'isp' | 'target',
  title: string,
  line: CqVerdictLine | undefined,
  badMs: number | undefined,
  baseline: CqBaseline | null,
): CqCard {
  const base = { key, title, category: 'connection' as const }
  if (line?.silent) return { ...base, state: 'ok', value: 'no ping', detail: 'does not answer ping (normal on some routers)' }
  if (!line || line.sent <= 0 || line.rtt_avg === null) return { ...base, state: 'none', value: '-', detail: 'no data' }
  const loss = line.loss_pct ?? 0
  const detail = `${round(loss, 1)}% loss${line.jitter !== null ? ` · ${round(line.jitter, 1)} ms jitter` : ''}`
  let state: CqCardState = 'ok'
  if (line.bad) state = 'bad'
  else if (loss > 0 || (badMs !== undefined && line.rtt_avg >= badMs * WARN_SHARE) || baseline?.above) state = 'warn'
  return { ...base, state, value: `${round(line.rtt_avg)} ms`, detail }
}

/** Cardurile de rezumat, din ce a adus deja ecranul (pagina, viteza). */
export function summaryCards(page: CqPage, speed?: CqSpeedPage | null): CqCard[] {
  const cards: CqCard[] = LINES.map((l) =>
    lineCard(
      l.key,
      l.name,
      page.verdict.lines[l.key],
      page.thresholds.latency_bad_ms[l.key],
      l.key === 'target' ? page.baseline.target : l.key === 'gateway' ? page.baseline.gateway : null,
    ),
  )

  /* MOS: 4 si peste = bun, 3.6 - 4 = acceptabil, sub 3.6 = convorbiri proaste. */
  const mos = average(page.latency.target.map((p) => p.mos))
  cards.push(
    mos === null
      ? { key: 'mos', title: 'Call quality', state: 'none', value: '-', detail: 'no data', category: 'connection' }
      : {
          key: 'mos',
          title: 'Call quality',
          state: mos >= 4 ? 'ok' : mos >= 3.6 ? 'warn' : 'bad',
          value: `MOS ${round(mos, 1)}`,
          detail: 'average to the target',
          category: 'connection',
        },
  )

  const wifi = page.wifi
  if (!wifi) {
    cards.push({ key: 'wifi', title: 'Wi-Fi', state: 'none', value: '-', detail: 'no data', category: 'wifi' })
  } else {
    const c = wifi.current
    const signal = c.signal
    const parts = [wifi.ap?.label, c.band ? `${c.band} GHz` : null].filter(Boolean)
    cards.push({
      key: 'wifi',
      title: 'Wi-Fi',
      state: signal === null ? 'none' : signal === 'good' ? 'ok' : signal === 'poor' ? 'bad' : 'warn',
      value: c.rssi_dbm === null ? '-' : `${round(c.rssi_dbm)} dBm`,
      detail: parts.length ? parts.join(' · ') : 'access point unknown',
      category: 'wifi',
    })
  }

  const total = average(page.http.map((p) => p.total_ms))
  const requests = page.http.reduce((n, p) => n + p.requests, 0)
  const errors = page.http.reduce((n, p) => n + p.errors, 0)
  if (total === null && requests === 0) {
    cards.push({ key: 'https', title: 'HTTPS', state: 'none', value: '-', detail: 'no data', category: 'web' })
  } else {
    cards.push({
      key: 'https',
      title: 'HTTPS',
      state: requests > 0 && errors >= requests ? 'bad' : errors > 0 || (total !== null && total >= 1000) ? 'warn' : 'ok',
      value: total === null ? '-' : `${round(total)} ms`,
      detail: errors > 0 ? `${errors} failed of ${requests}` : 'total request time',
      category: 'web',
    })
  }

  const retrans = average(page.tcp.map((p) => p.retrans_pct))
  cards.push(
    retrans === null
      ? { key: 'tcp', title: 'TCP retransmissions', state: 'none', value: '-', detail: 'no data', category: 'web' }
      : {
          key: 'tcp',
          title: 'TCP retransmissions',
          state: retrans >= 3 ? 'bad' : retrans >= 1 ? 'warn' : 'ok',
          value: `${tcpFormat(retrans)}%`,
          detail: 'average in this period',
          category: 'web',
        },
  )

  cards.push(...speedCards(speed?.lastGood ?? null))
  return cards
}

const mbps = (n: number) => (n >= 100 ? round(n) : round(n, 1))

/**
 * Categoria Speed: cate un card pe cifra a ultimului test reusit. Numai
 * bufferbloat-ul are stare (dupa nota A-F, pusa pe server); celelalte sunt
 * cifre fara prag, deci fara culoare.
 */
export function speedCards(good: CqSpeedTest | null): CqCard[] {
  const card = (key: string, title: string, value: string | null, detail: string, state: CqCardState = 'plain'): CqCard =>
    value === null
      ? { key, title, state: 'none', value: '-', detail: 'no data', category: 'speed' }
      : { key, title, state, value, detail, category: 'speed' }
  if (!good) {
    return [
      card('speed-down', 'Download', null, ''),
      card('speed-up', 'Upload', null, ''),
      card('speed-latency', 'Latency idle / under load', null, ''),
      card('speed-rpm', 'Responsiveness', null, ''),
      card('speed-bloat', 'Bufferbloat', null, ''),
    ]
  }
  const when = `last test ${good.at.slice(0, 16)}`
  const loaded = [good.loaded_down_ms, good.loaded_up_ms].filter((v): v is number => v !== null)
  const worst = loaded.length ? Math.max(...loaded) : null
  const ms = (n: number | null) => (n === null ? '-' : round(n))
  const grade = good.grade
  return [
    card('speed-down', 'Download', good.down_mbps === null ? null : `${mbps(good.down_mbps)} Mbps`, when),
    card('speed-up', 'Upload', good.up_mbps === null ? null : `${mbps(good.up_mbps)} Mbps`, when),
    card(
      'speed-latency',
      'Latency idle / under load',
      good.idle_ms === null && worst === null ? null : `${ms(good.idle_ms)} / ${ms(worst)} ms`,
      `under load ${ms(good.loaded_down_ms)} ms down, ${ms(good.loaded_up_ms)} ms up`,
    ),
    card('speed-rpm', 'Responsiveness', good.rpm === null ? null : `${round(good.rpm)} RPM`, 'round trips per minute, under load'),
    card(
      'speed-bloat',
      'Bufferbloat',
      grade === null ? null : `${grade}${good.bloat_ms !== null ? ` · +${round(good.bloat_ms)} ms` : ''}`,
      'extra latency under load',
      grade === 'A' || grade === 'B' ? 'ok' : grade === 'C' ? 'warn' : 'bad',
    ),
  ]
}

export function CqSummaryCards({ cards, onPick }: { cards: CqCard[]; onPick: (category: CqCategoryKey) => void }) {
  return (
    <div className="cq-sum" role="list" aria-label="Summary">
      {cards.map((c) => (
        <button
          key={c.key}
          type="button"
          role="listitem"
          className={`cq-sum-card is-${c.state}`}
          data-card={c.key}
          data-state={c.state}
          title={STATE_TEXT[c.state] ? `${c.title}: ${STATE_TEXT[c.state]}` : c.title}
          onClick={() => onPick(c.category)}
        >
          <span className="cq-sum-head">
            <span className="cq-sum-title">{c.title}</span>
            {STATE_TEXT[c.state] ? <span className="cq-sum-state">{STATE_TEXT[c.state]}</span> : null}
          </span>
          <span className="cq-sum-value">{c.value}</span>
          <span className="cq-sum-detail">{c.detail}</span>
        </button>
      ))}
    </div>
  )
}

function ExpandButton({ open, title, onToggle }: { open: boolean; title: string; onToggle: () => void }) {
  return (
    <button
      type="button"
      className="btn btn-xs btn-default cq-expand"
      aria-label={open ? `Close ${title}` : `Expand ${title}`}
      title={open ? 'Close (Esc)' : 'Expand'}
      aria-expanded={open}
      onClick={onToggle}
    >
      <i className={open ? 'fas fa-compress-alt' : 'fas fa-expand-alt'} aria-hidden="true"></i>
      {open ? ' Close' : ''}
    </button>
  )
}

/** Un grafic al grilei: se deschide pe toata latimea categoriei lui. */
function Panel({ id, open, children }: { id: CqPanelKey; open: boolean; children: ReactNode }) {
  return (
    <div id={`cq-panel-${id}`} className={`cq-panel${open ? ' is-expanded' : ''}`} data-panel={id} data-expanded={open ? 'yes' : 'no'}>
      {children}
    </div>
  )
}

/** Sectiune pe toata latimea (traseu, viteza, plan): intreaga, cu cardul ei, fara Expand. */
function WidePanel({ id, children }: { id: CqPanelKey; children: ReactNode }) {
  return (
    <div id={`cq-panel-${id}`} className="cq-panel cq-panel-wide" data-panel={id} data-wide="yes">
      {children}
    </div>
  )
}

/** O categorie: titlul, cardurile ei pe un rand, apoi graficele si panourile late. */
function Category({
  id,
  cards,
  onPick,
  children,
}: {
  id: CqCategoryKey
  cards: CqCard[]
  onPick: (category: CqCategoryKey) => void
  children: ReactNode
}) {
  const cat = CQ_CATEGORIES.find((c) => c.key === id)!
  const mine = cards.filter((c) => c.category === id)
  return (
    <section id={`cq-cat-${id}`} className={`cq-cat cq-cat-${id}`} data-category={id} aria-labelledby={`cq-cat-${id}-title`}>
      <h4 className="cq-cat-title" id={`cq-cat-${id}-title`}>
        <i className={cat.icon} aria-hidden="true"></i> {cat.title}
      </h4>
      {mine.length ? <CqSummaryCards cards={mine} onPick={onPick} /> : null}
      {children}
    </section>
  )
}

export function CqGrid({
  page,
  id,
  onZoom,
  onOpenSettings,
  long = false,
}: {
  page: CqPage
  id: number
  onZoom: (from: number, to: number) => void
  onOpenSettings?: () => void
  /** Vederea Full: acelasi design pe categorii, dar graficele lungi, cate unul pe rand, fara Expand. */
  long?: boolean
}) {
  const [expanded, setExpanded] = useState<CqPanelKey | null>(null)
  const speed = useQuery(cqSpeedQuery(id)).data
  const hasWhere = useHasFloorPosition(page.device.id)

  useEffect(() => {
    if (!expanded) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setExpanded(null)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [expanded])

  /* Clic pe card: derulare la categoria lui; nu se deschide nimic. */
  const pick = (category: CqCategoryKey) => {
    document.getElementById(`cq-cat-${category}`)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
  }
  const toggle = (key: CqPanelKey) => () => setExpanded((cur) => (cur === key ? null : key))
  const is = (key: CqPanelKey) => expanded === key

  const cards = summaryCards(page, speed)
  const { latency, loss, mos, http, tcp, common, httpCommon, markers, httpErrors } = cqSeries(page)
  const h = (key: CqPanelKey) => (long ? 320 : is(key) ? 400 : 220)
  const chart = (key: CqPanelKey, title: string, node: (height: number, actions: ReactNode) => ReactNode) => (
    <Panel id={key} open={!long && is(key)}>
      {node(h(key), long ? null : <ExpandButton open={is(key)} title={title} onToggle={toggle(key)} />)}
    </Panel>
  )

  const wifi = page.wifi
  const signal: CqChartSeries[] = wifi
    ? [{ name: 'Signal', color: CQ_COLORS[0], points: wifi.series.map((p) => ({ t: p.bucket, v: p.rssi, lo: p.rssi_min, hi: p.rssi_max })) }]
    : []
  const aps = wifi?.aps ?? []

  return (
    <div className={`cq-gridview${long ? ' is-long' : ''}`}>
      <CqVerdictBox page={page} />
      <CqChecksCategory checks={page.checks} />

      <CqChartSync onZoom={onZoom}>
        <Category id="connection" cards={cards} onPick={pick}>
          <div className="cq-grid">
            {chart('latency', 'Latency', (height, actions) => (
              <CqChart title="Latency (average, with min - max band)" unit="ms" series={latency} markers={markers} height={height} actions={actions} {...common} />
            ))}
            {chart('loss', 'Packet loss', (height, actions) => (
              <CqChart title="Packet loss" unit="%" series={loss} markers={markers} height={height} actions={actions} {...common} />
            ))}
            {chart('mos', 'Call quality', (height, actions) => (
              <CqChart title="Call quality to the target (MOS)" unit="MOS" series={mos} yMin={1} yMax={5} height={height} actions={actions} {...common} />
            ))}
          </div>
          {page.gatewaySilent ? (
            <p className="cq-note cq-grid-note">
              Router does not answer ping (normal on some routers) - it is left out of the loss chart; traffic goes through it.
            </p>
          ) : null}
        </Category>

        <Category id="wifi" cards={cards} onPick={pick}>
          {!wifi ? (
            <WidePanel id="wifi">
              <CqWifiNone />
            </WidePanel>
          ) : (
            /* Graficul de semnal in stanga, Access points in dreapta, pe acelasi
               rand; deschis, Wi-Fi-ul ia tot randul si AP-urile trec dedesubt. */
            <div className={`cq-wifi-row${aps.length ? '' : ' is-alone'}`}>
              {is('wifi') ? (
                <Panel id="wifi" open>
                  <div className="cq-panel-card">
                    <div className="cq-panel-tools">
                      <ExpandButton open title="Wi-Fi" onToggle={toggle('wifi')} />
                    </div>
                    <div className="cq-panel-body">
                      <CqWifi wifi={wifi} from={page.from} to={page.to} bucketS={page.bucket_s} onOpenSettings={onOpenSettings} tall />
                    </div>
                  </div>
                </Panel>
              ) : (
                chart('wifi', 'Wi-Fi', (height, actions) => (
                  <CqChart
                    title={`Wi-Fi signal${wifi.current.ssid ? ` · ${wifi.current.ssid}` : ''}`}
                    unit="dBm"
                    series={signal}
                    yMin={-100}
                    yMax={-20}
                    guides={signalGuides(wifi.thresholds)}
                    height={height}
                    actions={actions}
                    {...common}
                  />
                ))
              )}
              {aps.length > 0 ? (
                <div id="cq-panel-aps" className="cq-panel cq-panel-side" data-panel="aps" data-side="yes">
                  <CqAccessPoints aps={aps} />
                </div>
              ) : null}
            </div>
          )}
        </Category>

        <Category id="web" cards={cards} onPick={pick}>
          <div className="cq-grid">
            {chart('https', 'HTTPS request', (height, actions) => (
              <CqChart title="HTTPS request to the target" unit="ms" series={http} height={height} actions={actions} {...httpCommon} />
            ))}
            {chart('tcp', 'TCP retransmissions', (height, actions) => (
              <CqChart title="TCP retransmissions" unit="%" series={tcp} minTop={0.5} format={tcpFormat} height={height} actions={actions} {...common} />
            ))}
          </div>
          {httpErrors > 0 ? (
            <p className="cq-note cq-grid-note">
              {httpErrors === 1 ? '1 request' : `${httpErrors} requests`} to the target failed in this period.
            </p>
          ) : null}
        </Category>

        {/* Viteza si traseul au axa lor (30 de zile, ori niciuna): nu intra
            in zoom-ul ecranului, deci stau sub un sincron al lor, fara zoom. */}
        <CqChartSync>
          <Category id="speed" cards={cards} onPick={pick}>
            <WidePanel id="speed">
              <CqSpeed id={id} tiles={false} />
            </WidePanel>
          </Category>
          <Category id="path" cards={cards} onPick={pick}>
            <WidePanel id="path">
              <CqPath id={id} />
            </WidePanel>
          </Category>
        </CqChartSync>

        {hasWhere ? (
          <Category id="where" cards={cards} onPick={pick}>
            <WidePanel id="where">
              <DeviceFloorPosition id={page.device.id} currentPrefixes={aps.find((a) => a.current)?.prefixes ?? []} />
            </WidePanel>
          </Category>
        ) : null}
      </CqChartSync>
    </div>
  )
}
